// Searchable, server-paged picker for the large master lists (vendors ~2,700).
// Used in the Import Control edit modal for vendor / company / GL account.
// On pick it returns both the id and the canonical name (app.md §9 step 5).
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  Combobox,
  Option,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { popupPositionProps } from './popupPositioning'

export interface EntityOption {
  id: string
  primary: string
  secondary?: string
}

interface Props {
  /** Current selection (id + name) if any. */
  value?: { id?: string; name?: string }
  search: (term: string) => Promise<EntityOption[]>
  onSelect: (option: EntityOption) => void
  placeholder?: string
  disabled?: boolean
  /** Match surrounding controls (e.g. small inputs in the line items table). */
  size?: 'small' | 'medium' | 'large'
  /**
   * A persistent, always-visible label above the field, wired as the
   * Combobox's accessible name (`<label htmlFor>` against the input's own
   * id — `useId`-generated, same value used for the anti-autofill `name`).
   * Without this, the field's only name is its placeholder, which is exactly
   * what makes two or three unlabeled pickers stacked in one cell read as one
   * thing rather than two or three questions (the "should be two dropdowns"
   * report) — and disappears once a value is picked, whether or not another
   * label existed.
   *
   * Omit it where the caller already supplies a name some other way — a
   * surrounding Fluent `<Field label>` (Company/Vendor above,
   * `AssignmentsScreen`'s "Person"). `Field` renders its own
   * `<label htmlFor="field-«rN»__control">` and, through
   * `useFieldControlProps_unstable`, stamps that SAME generated id onto the
   * control's own input; it deliberately does NOT also set `aria-labelledby`,
   * because `useCombobox` reports `supportsLabelFor: true` and `Field` trusts
   * the id/htmlFor pairing alone. That is exactly why the `id` below is
   * conditional on `label` rather than unconditional: setting our own `id`
   * whenever this prop is omitted would win the merge in `inputSlotProps`'s
   * caller below (ours is spread last) and steal the input's id out from
   * under Field's `<label>`, which would then point at nothing — losing the
   * accessible name Field was supposed to provide, not gaining one.
   */
  label?: string
  /**
   * An accessible name with no visible chrome, for the rows that have no
   * vertical room for `label`. `AssociatedEntitiesEditor`'s grid is one line
   * with `alignItems: center`, and a caption there pushes the input below the
   * source text it is aligned with. It also lets the name say what the FIELD
   * does rather than repeating a word already on screen: the row's first cell
   * already reads "Vehicle", so the picker beside it wants "Vehicle to tag",
   * not "Vehicle" a second time.
   *
   * Sets `aria-label` only — no `id`, so a surrounding `Field` keeps its
   * `htmlFor` pairing. Pass this OR `label`, never both: two names on one
   * control is the defect the conditional `id` above exists to prevent.
   */
  ariaLabel?: string
  /**
   * Where to mount the listbox, overriding self-resolution (below). Pass the
   * enclosing `DialogSurface` element explicitly when the picker's own lookup
   * would be wrong or premature for the caller's timing (see
   * `AssignmentsScreen`, which keeps its own `dialogSurface` ref and passes it
   * here — redundant with self-resolution once the ref has fired, but harmless,
   * since an explicit value always wins). Passing `undefined` (the default) lets
   * `EntityPicker` find its own dialog host; passing `null` forces the inline
   * fallback even inside a dialog.
   */
  mountNode?: HTMLElement | null
}

// Empty-term ("top results") responses shared across pickers with the same
// search fn, so opening a many-line invoice doesn't fire one query per picker.
// Keyed weakly by the fn identity; entries drop when the fn is GC'd.
const emptyResultsCache = new WeakMap<Props['search'], EntityOption[]>()

const useStyles = makeStyles({
  hint: { padding: '6px 12px', color: tokens.colorNeutralForeground3 },
  // Wraps the label (when given) and the Combobox. Always present, whether or
  // not `label` is, so the DOM shape doesn't shift between the two — width and
  // shrink behavior mirror `combobox` below so a caller that used to size the
  // Combobox directly (`pickerStack`, `bulkPicker`) sizes this the same way.
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, width: '100%' },
  // Same 12px caption size as the picker's own text (size="small" Combobox).
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  // Fluent's Combobox defaults to ~250px min-width; clear it so the picker fills
  // its container (e.g. a line item's Account cell) instead of overrunning the next.
  // The root is an inline-grid of [input 1fr | chevron auto]; without min-width 0
  // the input can't shrink below its intrinsic width, which pushes the chevron
  // outside the root's border in narrow containers.
  combobox: {
    width: '100%',
    minWidth: 0,
    '& input': { minWidth: 0, width: '100%' },
  },
  // This styling is for the FALLBACK path now: `EntityPicker` resolves its own
  // dialog host (below) and portals there via `mountNode` whenever one exists,
  // so most instances never render this inline at all. It still has to look
  // right when one doesn't — no dialog ancestor, or the ref hasn't resolved
  // yet — and inline is where the narrow (Account/Company) cell width and
  // truncated option text used to show up. Give it a comfortable min-width so
  // names + codes are readable; it overlays neighboring columns like any
  // dropdown.
  //
  // The rest is legibility, reported from the Assignments dialog back when
  // inline was the only path: an INLINE listbox inherits its container's
  // surface, so inside a dialog it blended into the dialog and ran past the
  // content box unbounded. An explicit raised surface plus a bounded,
  // self-scrolling height makes it read as a dropdown wherever it opens.
  listbox: {
    minWidth: '340px',
    maxWidth: '92vw',
    // Owns the height, because `autoSize` is off in `positioning` below.
    maxHeight: '280px',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    // High, not 1: when inline (the fallback — no dialog host resolved) the
    // listbox shares a stacking context with the fields it overlays and with
    // anything below it in the dialog, and a z-index of 1 loses to any of them
    // that is itself positioned. Reported as "the z-index is off" — the options
    // rendered, underneath. Needed just as much when mounted at a resolved
    // dialog host, NOT only in the inline fallback: an explicit `mountNode`
    // disables Fluent's `usePortal` from supplying its own mount node, and
    // `usePortalMountNodeStyles` — the only place Fluent sets `zIndex: 1000000`
    // — fires only for that self-supplied node. A listbox mounted at the
    // dialog surface is therefore a z-auto sibling of anything else raised in
    // that same dialog (the tag-entities Popover, `entityPopSurface` at
    // `zIndex: 10` in `EditableInvoiceForm.tsx`, or the sticky action bar at
    // `zIndex: 2`) and loses without a layer of its own.
    zIndex: 1000,
  },
  // Two lines per row need room to separate; Fluent's default option padding is
  // tuned for one.
  option: { paddingTop: '6px', paddingBottom: '6px' },
  optionPrimary: { color: tokens.colorNeutralForeground1 },
  optionSecondary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
})

/**
 * Cast because the slot's type admits no `data-*` attributes, and these two are
 * the documented opt-outs for LastPass and Dashlane — attributes those
 * extensions read off the DOM, not React props with a declared type.
 */
function inputSlotProps(name: string): Record<string, unknown> {
  return {
    autoComplete: 'off',
    spellCheck: false,
    name,
    'data-lpignore': 'true',
    'data-form-type': 'other',
  }
}

export function EntityPicker({ value, search, onSelect, placeholder, disabled, size, mountNode, label, ariaLabel }: Props) {
  const s = useStyles()
  // The dialog host this picker resolves for itself, when the caller doesn't
  // hand one in. Read off the Combobox's OWN forwarded ref (`triggerRef`
  // below) — never a wrapper div: a wrapper wrapping the control here would
  // re-parent it inside `pickerStack` (InvoiceLineItemsTable) and `bulkPicker`
  // (same file), breaking the flex-shrink fix `s.combobox` above exists for.
  // `role="dialog"` only. `DialogSurface` can also emit `role="alertdialog"`
  // for `modalType="alert"`, but no dialog in this app uses that mode today —
  // CLAUDE.md's "build nothing speculative" rule, so this does not add a
  // second selector arm for a case nothing exercises. Add it back in the same
  // commit that adds the first `modalType="alert"` dialog.
  const triggerRef = useRef<HTMLInputElement>(null)
  const [selfMountNode, setSelfMountNode] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setSelfMountNode((triggerRef.current?.closest('[role="dialog"]') as HTMLElement | null) ?? null)
  }, [])
  // An explicit `mountNode` prop (even `null`, e.g. `AssignmentsScreen`'s ref
  // before it fires) always wins over self-resolution — only `undefined`
  // (the prop omitted) defers to it.
  const effectiveMountNode = mountNode !== undefined ? mountNode : selfMountNode
  // Defeats Chrome's field-name heuristic (see the `input` slot below): with a
  // per-instance nonsense name there is no "Person"-shaped field to match saved
  // form history against.
  const fieldName = `entity-search-${useId()}`
  const valueName = value?.name ?? ''
  const [query, setQuery] = useState(valueName)
  const [options, setOptions] = useState<EntityOption[]>([])
  const [loading, setLoading] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // Only the latest search may write options — a slow earlier response (e.g. the
  // first open-term query) must not overwrite a newer one.
  const searchSeq = useRef(0)
  const didInitialFetch = useRef(false) // lazy: first fetch waits for focus/open
  const editing = useRef(false) // input focused — don't clobber in-progress typing
  const typedWithoutSelect = useRef(false) // freeform text not yet committed to a pick

  const runSearch = useCallback(
    (term: string) => {
      const my = ++searchSeq.current
      const cached = term === '' ? emptyResultsCache.get(search) : undefined
      if (cached) {
        setOptions(cached)
        return
      }
      setLoading(true)
      search(term)
        .then((opts) => {
          if (term === '') emptyResultsCache.set(search, opts)
          if (my === searchSeq.current) setOptions(opts)
        })
        .catch(() => { if (my === searchSeq.current) setOptions([]) })
        .finally(() => { if (my === searchSeq.current) setLoading(false) })
    },
    [search],
  )

  // Resync the displayed text when the selection changes upstream (e.g. a header
  // company change repoints inheriting line items) — but never while the user
  // is typing into this field.
  useEffect(() => {
    if (!editing.current) setQuery(valueName)
  }, [valueName])

  // Clear a pending debounce on unmount.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  function ensureInitialFetch() {
    if (didInitialFetch.current) return
    didInitialFetch.current = true
    runSearch('')
  }

  function onInput(text: string) {
    setQuery(text)
    typedWithoutSelect.current = true
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => runSearch(text), 300)
  }

  return (
    <div className={s.fieldWrap}>
    {label && (
      <label htmlFor={fieldName} className={s.fieldLabel}>
        {label}
      </label>
    )}
    <Combobox
      // The listbox's home is resolved above (`effectiveMountNode`), not decided
      // here — this component no longer assumes inline is the default. A
      // resolved dialog host keeps the popup inside that dialog's focus trap
      // (portaled to `document.body`, the Fluent default, it would sit outside
      // the trap and tabster would haul focus back, slamming it shut —
      // intermittently, since it only bites when a re-render or the async open
      // race lands mid-interaction) AND mounts it at the surface's own stacking
      // level, above the fields it would otherwise overlay. No host — nothing
      // outside a dialog, or the ref hasn't resolved yet — falls back to
      // `inlinePopup`, which is now the exception case, not the norm; the same
      // two-line rationale covers `EditableInvoiceForm`'s Dropdown,
      // `AssociatedEntitiesEditor`'s Dropdown and `TrainingScreen`'s freeform
      // Combobox, which call `popupPositionProps` directly rather than
      // duplicating it.
      //
      // `positioning` rides along unconditionally inside that same helper: it is
      // not compensation for staying inline, it is what a portaled popup needs
      // too, since `autoSize` collapses either one to a single scrolling row
      // without it (reported from Assignments, 2026-08-06; screenshot
      // confirmed). Measuring against the window and turning `autoSize` off
      // means the height comes from the stylesheet (`s.listbox`) rather than
      // from whatever space happens to be left over.
      ref={triggerRef}
      // The BROWSER's own autofill dropdown, suppressed. Fluent sets no
      // `autocomplete` on the trigger input, so Chrome treats a field labelled
      // "Person" with placeholder "Search staff…" as a name field and offers its
      // saved form history, over the top of the real listbox. `autocomplete=off`
      // alone is advisory for name-shaped fields, so the per-instance `name`
      // removes the shape as well; the two `data-` attributes are the documented
      // opt-outs for LastPass and Dashlane. NOT `new-password`, which suppresses
      // reliably but makes Chrome offer to generate a password in a staff search.
      input={{
        ...inputSlotProps(fieldName),
        ...(label ? { id: fieldName } : {}),
        ...(!label && ariaLabel ? { 'aria-label': ariaLabel } : {}),
      }}
      {...popupPositionProps(effectiveMountNode)}
      listbox={{ className: s.listbox }}
      className={s.combobox}
      size={size}
      disabled={disabled}
      placeholder={placeholder ?? 'Search…'}
      value={query}
      selectedOptions={value?.id ? [value.id] : []}
      onFocus={() => { editing.current = true; ensureInitialFetch() }}
      onOpenChange={(_, data) => { if (data.open) ensureInitialFetch() }}
      onBlur={() => {
        editing.current = false
        // Freeform text the user typed but never committed to a pick saves
        // nothing — revert to the current selection so the field only ever shows
        // text that will be persisted.
        if (typedWithoutSelect.current) {
          typedWithoutSelect.current = false
          setQuery(valueName)
        }
      }}
      onInput={(e) => onInput((e.target as HTMLInputElement).value)}
      onOptionSelect={(_, data) => {
        const opt = options.find((o) => o.id === data.optionValue)
        if (opt) {
          typedWithoutSelect.current = false
          setQuery(opt.primary)
          onSelect(opt)
        }
      }}
      freeform
    >
      {loading && (
        <div className={s.hint}>
          <Spinner size="tiny" label="Searching…" />
        </div>
      )}
      {!loading && options.length === 0 && (
        <div className={s.hint}>No matches.</div>
      )}
      {options.map((o) => (
        <Option key={o.id} value={o.id} text={o.primary} className={s.option}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span className={s.optionPrimary}>{o.primary}</span>
            {o.secondary && <span className={s.optionSecondary}>{o.secondary}</span>}
          </div>
        </Option>
      ))}
    </Combobox>
    </div>
  )
}
